import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseEntity } from 'app/model';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { mapJsonObjectToClient, mapJsonObjectToServer } from '.';
import { extractData } from '../utils/entity-helper';
import { ApplicationInsights, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class TelemetryService {
  appInsights: ApplicationInsights;
  constructor(
    private router: Router,
    private authService: AuthService,
  ) {
    if (!environment.hasAiTelemtery) {
      return;
    }
    const angularPlugin = new AngularPlugin();
    const connstr =
      'InstrumentationKey=' +
      environment.aiInstrumentationKey +
      ';IngestionEndpoint=' +
      environment.aiIngestionEndpoint +
      ';LiveEndpoint=' +
      environment.aiLiveEndpoint;
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connstr,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router },
        },
        //        enableAutoRouteTracking: true, // option to log all route changes
      },
    });
    this.appInsights.loadAppInsights();
    const username = authService.getPracticeUserName();
    if (username) {
      const userid = authService.getPracticeUserId();
      this.appInsights.setAuthenticatedUserContext(username, userid + '', true);
    }
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags || [];
      const tag = environment.production ? 'prod' : 'test';
      envelope.tags['ai.cloud.role'] = 'saas_' + tag;
    });
  }

  logPageView(name?: string, url?: string): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  logExceptionMessage(errorname: string, description: string, severityLevel?: number): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackException({
      exception: { name: errorname, message: description },
      severityLevel: severityLevel,
    });
  }

  logAIException(exception: IExceptionTelemetry): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackException(exception);
  }

  logTrace(message: string, properties?: { [key: string]: any }): void {
    if (!environment.hasAiTelemtery) {
      return;
    }
    this.appInsights.trackTrace({ message: message }, properties);
  }
}
